import { useCallback, useEffect, useRef, useState } from 'react';

/** Belgi qancha turadi — o'qishga yetadi, keyingi nusxalashga xalaqit bermaydi. */
export const COPIED_RESET_MS = 1500;

export interface CopyToClipboard {
  /** Nusxalandi — muvaffaqiyatli bo'lsa `true`. Hech qachon xato TASHLAMAYDI. */
  copy: (value: string) => Promise<boolean>;
  /** Oxirgi nusxalash muvaffaqiyatli bo'ldi va belgi hali ko'rinib turibdi. */
  copied: boolean;
}

/**
 * `navigator.clipboard` FAQAT xavfsiz kontekstda bor.
 *
 * Ombor tarmog'idagi ichki manzil ko'pincha `http://` bo'ladi va u yerda
 * `navigator.clipboard` — `undefined`. Shuning uchun eskirgan
 * `document.execCommand('copy')` zaxira bo'lib qoladi: u aynan shu holat
 * uchun kerak va boshqa yo'l yo'q.
 *
 * Maydon ekrandan TASHQARIGA emas, `position: fixed` bilan ko'rinmas
 * qilinadi: `display: none` bo'lsa brauzer uni belgilay olmaydi, sahifadan
 * tashqarida bo'lsa esa `focus()` sahifani siljitib yuboradi.
 */
const copyByExecCommand = (value: string): boolean => {
  const field = document.createElement('textarea');
  field.value = value;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.top = '0';
  field.style.left = '0';
  field.style.opacity = '0';
  field.style.pointerEvents = 'none';

  document.body.appendChild(field);
  const selection = document.getSelection();
  // Odamning O'ZI belgilagan matni saqlanadi: nusxalash tugmasi uni
  // yo'qotib yuborsa, tugma belgilashning o'rnini bosmaydi — buzadi.
  const previous = selection !== null && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  try {
    field.select();
    field.setSelectionRange(0, value.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(field);
    if (previous !== null && selection !== null) {
      selection.removeAllRanges();
      selection.addRange(previous);
    }
  }
};

/**
 * Matnni clipboard ga ko'chiradi va qisqa vaqt «nusxalandi» holatini ushlaydi.
 *
 * Chaqiruvchi hech qachon `try/catch` yozmaydi: rad etilgan `Promise`
 * shu yerda yutiladi va `false` qaytadi. Ilgari uchta joyda
 * `void navigator.clipboard.writeText(v).then(...)` yozilgan edi — ularning
 * hech biri xatoni ushlamas, ruxsat berilmagan brauzerda esa konsolga
 * ushlanmagan rad etish tushardi.
 */
export const useCopyToClipboard = (): CopyToClipboard => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Komponent yopilganda taymer ham o'chadi — aks holda React yo'q
  // komponentga holat yozishga urinardi.
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(async (value: string): Promise<boolean> => {
    let ok = false;
    try {
      // API BOR-YO'QLIGI sinab ko'riladi, `isSecureContext` dan taxmin
      // qilinmaydi: xavfsiz kontekstda ham ruxsat rad etilishi mumkin va
      // o'shanda ham zaxira yo'l kerak bo'ladi. Yagona ishonchli belgi —
      // chaqiruvning O'ZI muvaffaqiyatli tugagani.
      if (navigator.clipboard?.writeText !== undefined) {
        await navigator.clipboard.writeText(value);
        ok = true;
      } else {
        ok = copyByExecCommand(value);
      }
    } catch {
      ok = copyByExecCommand(value);
    }

    if (ok) {
      setCopied(true);
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    }
    return ok;
  }, []);

  return { copy, copied };
};
